import InputAdornment from '@mui/material/InputAdornment';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

import type { SettingValue } from '../../api/types';

interface ManagedSettingProps<T> {
  setting: SettingValue<T> | undefined;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
  /// Given, the field becomes a choice between these rather than something to type into.
  choices?: { value: string; label: string }[];
  /// Held unchangeable for a reason other than the deployment - something the server is missing.
  /// The hint is what says which.
  disabled?: boolean;
}

/// A field for one setting. When the deployment sets it - a compose file, a unit file - the
/// field is shown as it is and cannot be edited, with a lock that says why when pointed at.
/// Letting someone type into a box whose value is thrown away on the next restart would be worse
/// than not offering the box. The variable's own name is not shown: it means something to
/// whoever wrote the compose file, and they know where it is.
export default function ManagedSetting<T>({
  setting,
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
  choices,
  disabled = false,
}: ManagedSettingProps<T>) {
  const { t } = useTranslation();
  const managed = setting?.isManagedByEnvironment ?? false;

  return (
    <Stack spacing={0.75}>
      <TextField
        label={label}
        value={value}
        type={choices ? undefined : type}
        select={choices !== undefined}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        disabled={managed || disabled}
        helperText={hint}
        slotProps={
          managed
            ? {
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title={t('settings.managed')}>
                        <LockOutlinedIcon fontSize="small" aria-label={t('settings.managed')} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }
            : undefined
        }
      >
        {choices?.map((choice) => (
          <MenuItem key={choice.value} value={choice.value}>
            {choice.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}
